/**
 * HelpGuidePage.jsx — RC 1.63 lightweight in-app user-guide renderer.
 *
 * Fetches a markdown file from /help/{guide}_user_guide.md (served as
 * a public static asset) and renders it with react-markdown +
 * remark-gfm. Images are resolved relative to /help/, so the
 * existing `assets/...` references in the markdown work without
 * modification.
 *
 * Three routes mount this component:
 *   /help/candidate  → candidate_user_guide.md
 *   /help/employer   → employer_user_guide.md
 *   /help/reviewer   → reviewer_user_guide.md
 *
 * A `?print=1` query flag renders a minimal print-friendly layout
 * used by the PDF export pipeline. No backend dependency.
 */
import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Loader2, AlertTriangle, Download } from 'lucide-react';

const GUIDE_FILES = {
  candidate: { file: 'candidate_user_guide.md', title: 'Candidate User Guide' },
  employer:  { file: 'employer_user_guide.md',  title: 'Employer User Guide'  },
  reviewer:  { file: 'reviewer_user_guide.md',  title: 'Reviewer User Guide'  },
};

export default function HelpGuidePage() {
  const { guide } = useParams();
  const [params] = useSearchParams();
  const printMode = params.get('print') === '1';

  const meta = GUIDE_FILES[guide];
  const [md, setMd] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    if (!meta) {
      setErr(`Unknown guide: "${guide}". Try /help/candidate, /help/employer, or /help/reviewer.`);
      return;
    }
    setMd(null);
    setErr(null);
    fetch(`/help/${meta.file}`)
      .then((r) => {
        if (!r.ok) throw new Error(`Failed to load guide (HTTP ${r.status})`);
        return r.text();
      })
      .then(setMd)
      .catch((e) => setErr(e.message || 'Failed to load guide.'));
  }, [guide, meta]);

  if (err) {
    return (
      <div
        className="max-w-3xl mx-auto p-8"
        data-testid={`help-guide-${guide}-error`}
      >
        <div className="flex items-start gap-3 p-5 rounded-lg border border-rose-500/40 bg-rose-500/5">
          <AlertTriangle className="h-5 w-5 text-rose-400 mt-0.5" />
          <div className="text-sm text-rose-100">{err}</div>
        </div>
      </div>
    );
  }

  if (md === null) {
    return (
      <div
        className="flex items-center gap-2 p-8 text-sm text-slate-300"
        data-testid={`help-guide-${guide}-loading`}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading {meta?.title || 'guide'}…
      </div>
    );
  }

  // Print mode renders without the chrome wrapper so the PDF
  // export pipeline gets a clean A4-friendly document.
  const wrapperCls = printMode
    ? 'help-guide-print bg-white text-slate-900'
    : 'mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 py-6 lg:py-10';

  return (
    <div className={wrapperCls} data-testid={`help-guide-${guide}`}>
      {!printMode && (
        <div className="mb-6 flex items-center justify-between gap-3">
          <h1 className="sr-only">{meta.title}</h1>
          <a
            href={`/help/${guide}_user_guide.pdf`}
            download
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-ndor-edge bg-ndor-surface-alt text-ndor-ink-secondary hover:text-ndor-ink"
            data-testid={`help-guide-${guide}-pdf-link`}
          >
            <Download className="h-3.5 w-3.5" />
            Download PDF
          </a>
        </div>
      )}
      <article className={`help-guide-prose ${printMode ? 'print-prose' : ''}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            img: ({ node, ...props }) => (
              <img
                {...props}
                loading="lazy"
                className={`my-5 rounded-lg border ${
                  printMode
                    ? 'border-slate-200 shadow-sm w-full'
                    : 'border-ndor-edge w-full'
                }`}
              />
            ),
            a: ({ node, ...props }) => (
              <a
                {...props}
                className="text-ndor-info hover:text-ndor-primary-hover underline"
                target={props.href?.startsWith('http') ? '_blank' : undefined}
                rel={props.href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              />
            ),
          }}
        >
          {md}
        </ReactMarkdown>
      </article>
    </div>
  );
}
